import { useDocumentTitle } from "@/lib/use-document-title";
import { useState } from "react";
import { StaffLayout } from "@/components/StaffLayout";
import { useMembers, useStores } from "@/lib/data-hooks";
import { MemberFilterPanel, useMemberFilter } from "@/components/MemberFilter";
import { supabase } from "@/integrations/supabase/client";
import { CheckCircle2, AlertTriangle } from "lucide-react";

export function SmsBody() {
  const { data: members = [] } = useMembers();
  const { data: stores = [] } = useStores();
  const { state, setters, filtered, clear } = useMemberFilter(members);

  const [message, setMessage] = useState("Hej! Tack för att du är medlem i Lagamobilen Access. Den här veckan får du 25% rabatt på alla skärmskydd – välkommen in!");
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);

  const withPhone = filtered.filter((m) => !!m.phone);
  const recipients = withPhone.length;
  const missingPhone = filtered.length - recipients;

  const send = async () => {
    if (!message || recipients === 0) return;
    setSending(true);
    try {
      const rows = withPhone.map((m) => ({ member_id: m.id, to_phone: m.phone!, message, status: "sent", template: "broadcast" }));
      if (rows.length) await supabase.from("sms_log").insert(rows);
      setSent(true);
      setTimeout(() => setSent(false), 3500);
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <div className="mb-6 p-4 rounded-xl border border-amber-500/40 bg-amber-500/10 flex items-start gap-3 text-sm">
        <AlertTriangle className="text-amber-600 shrink-0" size={16} />
        <div><strong className="font-display">SMS-utskick är inte aktiverat än.</strong> Meddelandet loggas men inga SMS skickas till kunderna.</div>
      </div>


      {sent && (
        <div className="mb-6 p-4 rounded-xl border border-[var(--color-success)]/40 bg-[var(--color-success)]/10 flex items-center gap-3">
          <CheckCircle2 className="text-[var(--color-success)]" />
          <div className="text-sm"><strong className="font-display">SMS skickat!</strong> Meddelandet har skickats till {recipients} mottagare.</div>
        </div>
      )}

      <div className="mb-5">
        <MemberFilterPanel state={state} setters={setters} stores={stores} members={members} filteredCount={filtered.length} onClear={clear} searchPlaceholder="Filtrera mottagare – sök i namn, telefon, intressen..." />
      </div>

      <div className="card-soft p-6 max-w-3xl">
        <h2 className="!text-lg mb-5">Meddelande</h2>
        <textarea
          className={`input-field min-h-[180px] ${message.length > 160 ? "input-error" : ""}`}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="Skriv ditt SMS här..."
        />
        <div className={`text-xs mt-2 ${message.length > 160 ? "text-destructive" : "text-muted-foreground"}`}>
          {message.length} / 160 tecken
        </div>

        <div className="mt-5 p-4 rounded-lg bg-muted text-sm space-y-1">
          <div>Skickar till <strong className="text-primary">{recipients}</strong> mottagare med telefonnummer.</div>
          {missingPhone > 0 && (
            <div className="flex items-center gap-2 text-xs text-muted-foreground"><AlertTriangle size={13} /> {missingPhone} av {filtered.length} matchande kunder saknar telefonnummer och hoppas över.</div>
          )}
        </div>
        <button onClick={send} disabled={sending || !message || recipients === 0 || message.length > 160} className="btn-pill mt-5 w-full sm:w-auto disabled:opacity-40 disabled:cursor-not-allowed">
          {sending ? "Skickar…" : "Skicka SMS"}
        </button>
      </div>
    </>
  );
}

export default function SmsPage() {
  useDocumentTitle("SMS-utskick — ÅF-Portal");
  return <StaffLayout title="SMS-utskick"><SmsBody /></StaffLayout>;
}
